import type { ConsoleSection } from '../../lib/api/operatorConsole';
import { presetLabel, sectionLabel } from './sectionLabels';

interface Props {
  sections: ConsoleSection[];
  presets: Record<string, ConsoleSection[]>;
  value: ConsoleSection[];
  onChange: (next: ConsoleSection[]) => void;
  disabled?: boolean;
}

/**
 * Section checkboxes with one-click starting points. A preset is a shortcut
 * for ticking boxes and nothing more — what gets stored is always the plain
 * list of sections, so a preset can be adjusted afterwards without the
 * selection turning into something the backend cannot express.
 */
export function PermissionPicker({ sections, presets, value, onChange, disabled }: Props) {
  const presetNames = Object.keys(presets);

  function toggle(section: ConsoleSection) {
    onChange(
      value.includes(section) ? value.filter((s) => s !== section) : [...value, section],
    );
  }

  return (
    <div>
      {presetNames.length > 0 && (
        <div className="mb-3 flex flex-wrap items-center gap-2">
          <span className="text-[11px] uppercase tracking-wide text-[var(--co-muted)]">
            Start from
          </span>
          {presetNames.map((name) => (
            <button
              key={name}
              type="button"
              disabled={disabled}
              onClick={() => onChange([...(presets[name] ?? [])])}
              className="rounded-full border border-[var(--co-border)] px-3 py-1 text-[12px] text-[var(--co-text)] transition-colors hover:bg-[var(--co-bg)] disabled:opacity-50"
            >
              {presetLabel(name)}
            </button>
          ))}
          <button
            type="button"
            disabled={disabled}
            onClick={() => onChange([])}
            className="rounded-full px-3 py-1 text-[12px] text-[var(--co-muted)] underline-offset-2 hover:underline disabled:opacity-50"
          >
            Clear
          </button>
        </div>
      )}

      <div className="grid gap-2 sm:grid-cols-2">
        {sections.map((section) => (
          <label
            key={section}
            className="flex items-center gap-2 text-[13px] text-[var(--co-text)]"
          >
            <input
              type="checkbox"
              checked={value.includes(section)}
              disabled={disabled}
              onChange={() => toggle(section)}
            />
            <span>{sectionLabel(section)}</span>
          </label>
        ))}
      </div>
    </div>
  );
}
